import { navigateTo } from '../router';
import { t, onLocaleChange, getLocale } from '../i18n';
import { apiRequest, apiUpload } from '../api';
import { state } from '../state';
import { ensureRolesLoaded } from '../app';
import { listRefs, MAP_TYPES, type GameMap, type GameMod, type MapType } from '../api/game-refs';
import { listMods, type ModSetting, type ModSettingPreset } from '../api/mods';
import type { PlayerWithStats, PlayersListResponse, Race } from '../types';
import {
    findReplayMap,
    matchReplayPlayer,
    normalizeReplayRace,
    stripClanTag,
    type LobbySetting,
    type ReplayPrefill,
} from '../ui/replay-import';
import { buildReel, type Reel, type ReelItem } from '../ui/reel';
import { buildModSettingsBox, presetToRoll, rollModSettings, toggleModSetting, type ModSettingRoll } from '../ui/mod-settings';
import { openModExclusionsModal, applyLocalExclusions, countModExclusions } from '../ui/mod-exclusions';
import { hideMapPreview, showMapPreview } from '../ui/map-preview';
import { buildLinkCopyButton } from '../ui/copy';
import { LEAGUE_DYNAMIC_TYPE, leagueMapIds, leagueMapTypes, mapTypesWithLeague, type MapFilterType } from '../ui/map-presets';
import { buildPoolPicker, mapToPoolItem, modToPoolItem, type PoolPicker } from '../ui/pool-picker';
import { MATCH_PREFILL_KEY, type MatchGamePrefill } from './games';
import { buildOverlayToggle, clearOverlayOutput, showOverlayPrediction, showOverlayRoll, startOverlayPredictionDraw, startOverlayRoll, type MapRollOutput, type MatchPredictOutput } from '../ui/overlay-output';
import { animateDrawRows } from '../ui/draw-anim';
import {
    buildRacePicker,
    fetchPrediction,
    renderPredictResults,
    drawWeightedWinner,
    type PredictResultRow,
} from './tools';

// ============================================================
// Инструмент «Менеджер матча» (мастер по этапам):
//   1. Карта и режим — компактные барабаны (переиспускают рандомайзер),
//      можно пропустить.
//   2. Участники — мини-список игроков (автокомплит + раса).
//   3. Прогноз — переиспользует инструмент «Прогноз победителя».
//   4. Режим стримера — открывает /control с закреплёнными игроками.
//   5. Добавить игру — передаёт дату/карту/мод/участников в форму новой
//      игры на /games (черновик через sessionStorage, без записи в БД здесь).
// ============================================================

interface MatchPlayer {
    id: number | null;
    name: string;
    race: Race | null;
}

/** Пресет пулов карт/модов (таблица map_presets, API /api/map-presets). */
interface MapPreset {
    id: number;
    name: string;
    shared: boolean;
    map_ids: number[];
    mod_ids: number[];
}

let cachedPlayers: PlayerWithStats[] = [];
let cachedMaps: GameMap[] = [];
let cachedMods: GameMod[] = [];
/** Настройки и пресеты модов (публичный /api/mods) — для рандомизации настроек. */
let cachedModSettings = new Map<number, ModSetting>();
/** Серверные (общие) настройки — к ним возвращает «Сбросить на умолчания». */
let cachedModSettingsBase = new Map<number, ModSetting>();
let cachedModPresets = new Map<number, ModSettingPreset[]>();

let presets: MapPreset[] = [];
/** Выбранный сохранённый пресет (ограничивает карты и моды). */
let activePresetId: number | null = null;
/** Динамический пресет: тип карты + число игроков либо объединение «FFA Лига» (моды не ограничивает). */
type DynamicType = MapType | typeof LEAGUE_DYNAMIC_TYPE;
let activeDynamic: { type: DynamicType; count: number | null } | null = null;
const PRESET_KEY = 'tools.match.preset';

let matchPlayers: MatchPlayer[] = [];
let participants: MatchPlayer[] = [];
let mapReel: Reel | null = null;
let modReel: Reel | null = null;
/** Выпавшие/выбранные карта и мод (сохраняются между заходами). */
let selectedMapId: number | null = null;
let selectedModId: number | null = null;
let stageMapSkipped = false;
/** Рандомизированные настройки выпавшего мода (сохраняются в состоянии мастера). */
let modRoll: ModSettingRoll | null = null;
/** Вычеркнутые только для этого матча карты и моды (списки/пресеты не меняются). */
let matchExcludedMaps: number[] = [];
let matchExcludedMods: number[] = [];
let rootEl: HTMLElement | null = null;
let playersBox: HTMLElement | null = null;
let summaryEl: HTMLElement | null = null;
let presetBoxEl: HTMLElement | null = null;
let presetListEl: HTMLElement | null = null;
/** Раскрытый список карт/модов выбранного пресета (по умолчанию скрыт). */
let presetListOpen = false;
let presetToggleEl: HTMLButtonElement | null = null;
let presetToggleLabelEl: HTMLElement | null = null;
let presetToggleCountEl: HTMLElement | null = null;
let predictResultsBox: HTMLElement | null = null;
let predictErrorBox: HTMLElement | null = null;
let streamerHintEl: HTMLElement | null = null;
let disposeLocale: (() => void) | null = null;
let lastDrawnWinner: number | null = null;
/** Отмена идущей прокрутки розыгрыша победителя. */
let cancelDraw: (() => void) | null = null;
let mapImageEl: HTMLElement | null = null;
/** Названия выпавших карты/мода под картинкой. */
let pickNamesEl: HTMLElement | null = null;
/** Блок настроек выпавшего мода на этапе 1. */
let modSettingsEl: HTMLElement | null = null;
let addGameAtInput: HTMLInputElement | null = null;
let addGameHintEl: HTMLElement | null = null;
/** Нижняя секция «Пул матча» (полные настройки, свёрнута по умолчанию). */
let poolDetailsEl: HTMLDetailsElement | null = null;
/** Настройка пула карт/модов — общий модуль с рандомайзером. */
let poolPickers: PoolPicker[] = [];
/** Пропускать дисбалансные карты (как в рандомайзере, по умолчанию да). */
let matchSkipUnbalanced = true;
/** Выбранные дата/время будущей игры (datetime-local), сохраняются при перерисовке. */
let addGameAt = '';

/** Импорт из реплея: зона приёма, кнопка и статус последнего разбора. */
let replayAbort: AbortController | null = null;
let replayDropZoneEl: HTMLElement | null = null;
let replayParsing = false;
let replayPanelEl: HTMLElement | null = null;
let replayImportBtn: HTMLButtonElement | null = null;
let replayStatusEl: HTMLElement | null = null;
type ReplayNoticePart = { key: Parameters<typeof t>[0]; params?: Record<string, string | number> };
let replayNotice: { kind: 'ok' | 'warn'; parts: ReplayNoticePart[] } | null = null;
/**
 * Опции лобби мода из импортированного реплея: уходят в форму игры вместе с черновиком.
 * Не сохраняются между визитами и сбрасываются после передачи, чтобы не попасть в следующий матч.
 */
let replayLobbySettings: LobbySetting[] = [];

const PLAYERS_BOX_ID = 'match-players';
/** Локально сохраняемое состояние мастера (участники, розыгрыш, дата). */
const STATE_KEY = 'tools.match.state';
const RACES: readonly Race[] = ['T', 'Z', 'P', 'R'];

interface PersistedMatchState {
    participants: MatchPlayer[];
    mapId: number | null;
    modId: number | null;
    skipped: boolean;
    addGameAt: string;
    excludedMaps: number[];
    excludedMods: number[];
    /** Пропускать дисбалансные карты (как в рандомайзере). */
    skipUnbalanced: boolean;
    /** Рандомизированные настройки выпавшего мода. */
    modRoll: ModSettingRoll | null;
}

/** Загруженное состояние + признак, что новый формат пула уже был сохранён. */
interface LoadedMatchState extends PersistedMatchState {
    poolsInitialized: boolean;
}

let saveTimer: number | null = null;

export async function mountMatch(): Promise<void> {
    showScreen('screen-match');

    // Реплей можно бросить в любое место страницы; слушатели живут до unmount.
    replayAbort?.abort();
    replayAbort = new AbortController();
    bindReplayDrop(replayAbort.signal);

    const backBtn = document.getElementById('btn-back-tools-match');
    if (backBtn) {
        const fresh = backBtn.cloneNode(true) as HTMLElement;
        backBtn.replaceWith(fresh);
        fresh.addEventListener('click', () => navigateTo('/tools', true));
    }

    const resetBtn = document.getElementById('btn-reset-match');
    if (resetBtn) {
        const fresh = resetBtn.cloneNode(true) as HTMLElement;
        resetBtn.replaceWith(fresh);
        fresh.addEventListener('click', () => resetMatch());
    }

    const root = document.getElementById('match-root');
    if (!root) return;
    rootEl = root;
    root.textContent = t('common.loading');

    // Восстанавливаем сохранённое состояние: участники, розыгрыш, пропуск, дата
    const saved = loadSavedState();
    matchPlayers = saved && saved.participants.length
        ? saved.participants.map((p) => ({ ...p }))
        : participants.length
            ? participants.map((p) => ({ ...p }))
            : [
                  { id: null, name: '', race: null },
                  { id: null, name: '', race: null },
              ];
    selectedMapId = saved?.mapId ?? null;
    selectedModId = saved?.modId ?? null;
    stageMapSkipped = saved?.skipped ?? false;
    modRoll = saved?.modRoll ?? null;
    matchExcludedMaps = saved?.excludedMaps ?? [];
    matchExcludedMods = saved?.excludedMods ?? [];
    matchSkipUnbalanced = saved?.skipUnbalanced ?? true;
    addGameAt = saved?.addGameAt || toDatetimeLocal(new Date());

    try {
        const [players, maps, mods] = await Promise.all([
            apiRequest<PlayersListResponse>('/api/players').then((r) => r.players),
            listRefs<GameMap>('maps'),
            listRefs<GameMod>('mods'),
            loadPresets(),
            loadModSettings(),
        ]);
        cachedPlayers = players;
        // Мягко удалённые карты в розыгрыше не участвуют
        cachedMaps = maps.filter((m) => !m.deleted_at);
        cachedMods = mods;
    } catch (err) {
        console.error('[tools/match] failed to load refs:', err);
    }

    // Восстанавливаем выбранный пресет (если он ещё существует)
    try {
        applyPresetValue(localStorage.getItem(PRESET_KEY) ?? '');
    } catch {
        activePresetId = null;
        activeDynamic = null;
    }
    // Миграция: в старом сохранении не было полного пула — засеваем его пресетом
    if (!saved || !saved.poolsInitialized) seedExclusionsFromPreset();

    buildUI();
    // Роли нужны, чтобы показать/скрыть кнопки «Добавить игру» и импорт реплея.
    void ensureRolesLoaded().then(() => {
        refreshAddGameState();
        refreshReplayImportState();
    });
}

/** Строка для input[type=datetime-local] из Date. */
function toDatetimeLocal(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return (
        `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
        `T${pad(date.getHours())}:${pad(date.getMinutes())}`
    );
}

// ============================================================
// Локальное сохранение состояния
// ============================================================
/** Читает сохранённое состояние (с мягкой валидацией). */
function loadSavedState(): LoadedMatchState | null {
    try {
        const raw = localStorage.getItem(STATE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<PersistedMatchState>;
        const participants = Array.isArray(parsed.participants)
            ? parsed.participants
                  .filter((p) => p && typeof p === 'object')
                  .map((p) => ({
                      id: typeof p.id === 'number' ? p.id : null,
                      name: typeof p.name === 'string' ? p.name : '',
                      race: RACES.includes(p.race as Race) ? (p.race as Race) : null,
                  }))
            : [];
        const ids = (value: unknown): number[] =>
            Array.isArray(value) ? value.filter((x): x is number => typeof x === 'number') : [];
        const modRoll = parseModRoll(parsed.modRoll);
        return {
            participants,
            mapId: typeof parsed.mapId === 'number' ? parsed.mapId : null,
            modId: typeof parsed.modId === 'number' ? parsed.modId : null,
            skipped: parsed.skipped === true,
            addGameAt: typeof parsed.addGameAt === 'string' ? parsed.addGameAt : '',
            excludedMaps: ids(parsed.excludedMaps),
            excludedMods: ids(parsed.excludedMods),
            // Старые сохранения не знали флага — по умолчанию пропускаем дисбалансные
            skipUnbalanced: parsed.skipUnbalanced !== false,
            modRoll,
            // Старый формат (до полного пула) не содержал этих полей
            poolsInitialized:
                Array.isArray(parsed.excludedMaps) || Array.isArray(parsed.excludedMods),
        };
    } catch {
        return null;
    }
}

/** Мягко валидирует сохранённые настройки мода (modId + значения параметров). */
function parseModRoll(value: unknown): ModSettingRoll | null {
    if (!value || typeof value !== 'object') return null;
    const raw = value as { modId?: unknown; params?: unknown; mode?: unknown; presetName?: unknown };
    if (typeof raw.modId !== 'number' || !Array.isArray(raw.params)) return null;
    const params = raw.params
        .filter((p): p is Record<string, unknown> => !!p && typeof p === 'object')
        .map((p) => ({
            name: typeof p.name === 'string' ? p.name : '',
            value: typeof p.value === 'string' ? p.value : '',
            // Совместимость со старым форматом, где было поле fixed
            enabled: typeof p.enabled === 'boolean' ? p.enabled : p.fixed !== true,
        }))
        .filter((p) => p.name);
    return {
        modId: raw.modId,
        params,
        mode: raw.mode === 'preset' ? 'preset' : 'random',
        presetName: typeof raw.presetName === 'string' ? raw.presetName : null,
    };
}

/** Сохраняет текущее состояние мастера. */
function saveState(): void {
    try {
        const data: PersistedMatchState = {
            participants: matchPlayers,
            mapId: selectedMapId,
            modId: selectedModId,
            skipped: stageMapSkipped,
            addGameAt,
            excludedMaps: matchExcludedMaps,
            excludedMods: matchExcludedMods,
            skipUnbalanced: matchSkipUnbalanced,
            modRoll,
        };
        localStorage.setItem(STATE_KEY, JSON.stringify(data));
    } catch {
        /* без localStorage */
    }
}

/** Отложенное сохранение — чтобы не писать на каждый введённый символ. */
function scheduleStateSave(): void {
    if (saveTimer !== null) window.clearTimeout(saveTimer);
    saveTimer = window.setTimeout(() => {
        saveTimer = null;
        saveState();
    }, 300);
}

/** Полный сброс мастера и сохранённого выбора. */
function resetMatch(): void {
    if (!confirm(t('tools.match_reset_confirm'))) return;
    if (saveTimer !== null) {
        window.clearTimeout(saveTimer);
        saveTimer = null;
    }
    try {
        localStorage.removeItem(STATE_KEY);
        localStorage.removeItem(PRESET_KEY);
    } catch {
        /* ignore */
    }
    participants = [];
    matchPlayers = [
        { id: null, name: '', race: null },
        { id: null, name: '', race: null },
    ];
    selectedMapId = null;
    selectedModId = null;
    stageMapSkipped = false;
    modRoll = null;
    matchExcludedMaps = [];
    matchExcludedMods = [];
    cancelDraw?.();
    cancelDraw = null;
    lastDrawnWinner = null;
    lastPredictRows = [];
    // Сброс — убираем виджет с оверлея
    void clearOverlayOutput();
    activePresetId = null;
    activeDynamic = null;
    addGameAt = toDatetimeLocal(new Date());
    replayNotice = null;
    replayLobbySettings = [];
    matchSkipUnbalanced = true;
    presetListOpen = false;
    buildUI();
}

export function unmountMatch(): void {
    // Сохраняем перед уходом со страницы (в т.ч. незавершённый ввод)
    if (saveTimer !== null) {
        window.clearTimeout(saveTimer);
        saveTimer = null;
    }
    saveState();
    hideMapPreview();

    replayAbort?.abort();
    replayAbort = null;
    replayDropZoneEl = null;
    replayPanelEl = null;
    replayImportBtn = null;
    replayStatusEl = null;
    replayParsing = false;
    replayNotice = null;
    replayLobbySettings = [];

    disposeLocale?.();
    disposeLocale = null;
    participants = matchPlayers.map((p) => ({ ...p }));
    matchPlayers = [];
    mapReel = null;
    modReel = null;
    rootEl = null;
    playersBox = null;
    summaryEl = null;
    mapImageEl = null;
    pickNamesEl = null;
    modSettingsEl = null;
    presetBoxEl = null;
    presetListEl = null;
    presetToggleEl = null;
    presetToggleLabelEl = null;
    presetToggleCountEl = null;
    predictResultsBox = null;
    predictErrorBox = null;
    streamerHintEl = null;
    addGameAtInput = null;
    addGameHintEl = null;
    poolDetailsEl = null;
    poolPickers = [];
    stageMapSkipped = false;
    cancelDraw?.();
    cancelDraw = null;
    lastDrawnWinner = null;
}

function showScreen(id: string): void {
    document.querySelectorAll<HTMLElement>('.screen').forEach((el) => {
        el.classList.toggle('hidden', el.id !== id);
    });
}

// ============================================================
// Сборка страницы
// ============================================================
function buildUI(): void {
    const root = rootEl;
    if (!root) return;
    root.innerHTML = '';
    disposeLocale?.();

    // Два барабана: карта и мод (как в инструменте рандомизации)
    mapReel = buildReel(mapItems, t('tools.random_map'), t('tools.random_empty'));
    modReel = buildReel(modItems, t('tools.random_mod'), t('tools.random_empty'));
    // Восстанавливаем прошлый розыгрыш (в т.ч. после перерисовки из-за языка)
    mapReel.setSelected(selectedMapId);
    modReel.setSelected(selectedModId);

    root.appendChild(buildMapStage());
    root.appendChild(buildPlayersStage());
    root.appendChild(buildPredictStage());
    root.appendChild(buildStreamerStage());
    root.appendChild(buildAddGameStage());
    renderPlayers();

    disposeLocale = onLocaleChange(() => buildUI());
}

/** Секция-этап: номер (необязательный), заголовок и содержимое. */
function buildStage(
    num: string | null,
    titleKey: Parameters<typeof t>[0]
): { section: HTMLElement; body: HTMLElement } {
    const section = document.createElement('section');
    section.className = 'card match-stage';

    const header = document.createElement('div');
    header.className = 'match-stage-head';
    const badge = document.createElement('span');
    badge.className = 'match-stage-num';
    badge.textContent = num ?? '';
    const title = document.createElement('h3');
    title.className = 'match-stage-title';
    title.textContent = t(titleKey);
    if (num) header.appendChild(badge);
    header.appendChild(title);
    section.appendChild(header);

    const body = document.createElement('div');
    body.className = 'match-stage-body';
    section.appendChild(body);
    return { section, body };
}

// ---------- Импорт из реплея ----------
/** Импорт доступен тем же ролям, что и создание игры (сервер тоже проверяет). */
function canImportReplay(): boolean {
    return canAddGame();
}

function buildReplayImportPanel(): HTMLElement {
    const panel = document.createElement('div');
    panel.className = 'match-add-game-replay';

    const title = document.createElement('div');
    title.className = 'match-add-game-replay-title';
    title.textContent = `🎬 ${t('tools.match_replay_title')}`;

    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.SC2Replay,.replay';
    fileInput.className = 'hidden';
    fileInput.addEventListener('change', () => {
        const file = fileInput.files?.[0];
        fileInput.value = '';
        if (file) openReplayFromFile(file);
    });

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'btn-secondary match-replay-btn';
    btn.addEventListener('click', () => {
        if (canImportReplay() && !replayParsing) fileInput.click();
    });

    const hint = document.createElement('div');
    hint.className = 'match-replay-hint';
    hint.textContent = t('tools.match_add_game_replay_hint');

    const status = document.createElement('div');
    status.className = 'match-replay-status hidden';

    replayPanelEl = panel;
    replayImportBtn = btn;
    replayStatusEl = status;

    panel.append(title, btn, fileInput, hint, status);
    refreshReplayImportState();
    return panel;
}

/** Панель и кнопка видны только тем, кто может добавлять игры. */
function refreshReplayImportState(): void {
    const allowed = canImportReplay();
    replayPanelEl?.classList.toggle('hidden', !allowed);
    if (replayImportBtn) {
        replayImportBtn.disabled = !allowed || replayParsing;
        replayImportBtn.textContent = replayParsing
            ? t('games.replay_parsing')
            : t('tools.match_replay_choose');
    }
    renderReplayNotice();
}

function renderReplayNotice(): void {
    if (!replayStatusEl) return;
    if (!replayNotice || replayNotice.parts.length === 0) {
        replayStatusEl.classList.add('hidden');
        replayStatusEl.textContent = '';
        return;
    }
    replayStatusEl.textContent = replayNotice.parts
        .map((part) => t(part.key, part.params))
        .join(' ');
    replayStatusEl.classList.toggle('is-warn', replayNotice.kind === 'warn');
    replayStatusEl.classList.toggle('is-ok', replayNotice.kind === 'ok');
    replayStatusEl.classList.remove('hidden');
}

function setReplayDropZone(on: boolean, parsing = false): void {
    if (!replayDropZoneEl) return;
    replayDropZoneEl.classList.toggle('hidden', !on);
    replayDropZoneEl.classList.toggle('is-parsing', parsing);
}

/** Файл реплея можно бросить в любое место страницы (как на «Играх»). */
function bindReplayDrop(signal: AbortSignal): void {
    replayDropZoneEl = document.getElementById('match-replay-dropzone');
    if (!replayDropZoneEl) return;

    const hasFiles = (e: DragEvent): boolean =>
        Array.from(e.dataTransfer?.types ?? []).includes('Files');
    const canHandle = (e: DragEvent): boolean =>
        hasFiles(e) && canImportReplay() && !replayParsing;

    // dragenter/dragleave приходят и на дочерние элементы — считаем глубину,
    // иначе зона мигает при движении курсора.
    let dragDepth = 0;

    document.addEventListener('dragenter', (e) => {
        if (!canHandle(e)) return;
        e.preventDefault();
        dragDepth += 1;
        setReplayDropZone(true);
    }, { signal });

    document.addEventListener('dragover', (e) => {
        if (!canHandle(e)) return;
        // Без preventDefault браузер уйдёт по адресу брошенного файла.
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    }, { signal });

    document.addEventListener('dragleave', (e) => {
        if (!hasFiles(e)) return;
        dragDepth = Math.max(0, dragDepth - 1);
        if (dragDepth === 0) setReplayDropZone(false);
    }, { signal });

    document.addEventListener('drop', (e) => {
        if (!canHandle(e)) return;
        dragDepth = 0;
        setReplayDropZone(false);
        e.preventDefault();
        const file = e.dataTransfer?.files?.[0];
        if (file) openReplayFromFile(file);
    }, { signal });

    // Страховка: если файл увели за пределы окна, dragleave может не прийти.
    window.addEventListener('blur', () => {
        dragDepth = 0;
        setReplayDropZone(false);
    }, { signal });
}

function openReplayFromFile(file: File): void {
    if (!/\.(sc2replay|replay)$/i.test(file.name)) {
        alert(t('games.replay_not_replay'));
        return;
    }
    if (file.size > 25 * 1024 * 1024) {
        alert(t('games.replay_too_large'));
        return;
    }
    void importMatchReplay(file);
}

async function importMatchReplay(file: File): Promise<void> {
    if (replayParsing) return;
    replayParsing = true;
    refreshReplayImportState();
    setReplayDropZone(true, true);

    try {
        const res = await apiUpload<{ replay: ReplayPrefill }>(
            '/api/games/parse-replay',
            file,
            file.name,
            state.token,
            replayAbort?.signal
        );
        applyReplayToMatch(res.replay);
    } catch (err) {
        // Ушли со страницы — разбор отменён, ошибку не показываем.
        if (replayAbort?.signal.aborted) return;
        alert(t('games.replay_parse_error') + (err instanceof Error ? err.message : String(err)));
    } finally {
        replayParsing = false;
        setReplayDropZone(false);
        refreshReplayImportState();
    }
}

/**
 * Заполняет мастер данными из реплея: участники (с расами и сопоставлением
 * по нику/aka), карта и дата. Мод в реплее не указан — оставляем выбранный.
 */
function applyReplayToMatch(prefill: ReplayPrefill): void {
    const ordered = prefill.players
        .map((p, index) => ({ p, index }))
        .sort((a, b) => {
            const aWin = String(a.p.result ?? '').toLowerCase() === 'win';
            const bWin = String(b.p.result ?? '').toLowerCase() === 'win';
            if (aWin !== bWin) return aWin ? -1 : 1;
            const aScore = typeof a.p.score === 'number' ? a.p.score : -1;
            const bScore = typeof b.p.score === 'number' ? b.p.score : -1;
            if (aScore !== bScore) return bScore - aScore;
            return a.index - b.index;
        })
        .map(({ p }) => p);

    const unresolved: string[] = [];
    matchPlayers = ordered.map((p) => {
        const matched = matchReplayPlayer(cachedPlayers, p.name);
        const cleanName = p.name ? stripClanTag(p.name) : '';
        if (!matched && cleanName) unresolved.push(cleanName);
        return {
            id: matched?.id ?? null,
            name: matched?.name ?? cleanName,
            race: p.race ? normalizeReplayRace(p.race) : (matched?.dominant_race ?? null),
        };
    });
    if (matchPlayers.length === 0) {
        matchPlayers = [
            { id: null, name: '', race: null },
            { id: null, name: '', race: null },
        ];
    }

    // Карта: сервер уже искал по name/alt_name; на всякий случай повторяем локально.
    const prefillMap = (prefill.map as GameMap | null | undefined)
        ?? findReplayMap(cachedMaps, prefill.mapTitle);
    if (prefillMap) {
        if (!cachedMaps.some((m) => m.id === prefillMap.id)) {
            cachedMaps = [...cachedMaps, prefillMap as GameMap]
                .sort((a, b) => a.name.localeCompare(b.name));
        }
        // Карта из реплея могла быть вычеркнута в пуле матча — возвращаем её.
        matchExcludedMaps = matchExcludedMaps.filter((id) => id !== prefillMap.id);
        selectedMapId = prefillMap.id;
        stageMapSkipped = false;
    }

    if (prefill.playedAtMs && Number.isFinite(prefill.playedAtMs)) {
        addGameAt = toDatetimeLocal(new Date(prefill.playedAtMs));
    }
    replayLobbySettings = prefill.lobbySettings ?? [];

    const parts: ReplayNoticePart[] = [{ key: 'tools.match_replay_applied' }];
    if (prefill.duplicate) {
        // Игра с этим реплеем уже есть — предупреждаем до сохранения.
        const when = prefill.duplicate.played_at ? new Date(prefill.duplicate.played_at) : null;
        parts.push(when && !Number.isNaN(when.getTime())
            ? {
                key: 'games.replay_duplicate',
                params: {
                    date: when.toLocaleString(getLocale(), {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                    }),
                },
            }
            : { key: 'games.replay_duplicate_no_date' });
    }
    if (unresolved.length > 0) {
        parts.push({ key: 'games.replay_unresolved', params: { names: unresolved.join(', ') } });
    }
    if (!prefillMap && prefill.mapTitle) {
        parts.push({ key: 'games.replay_map_not_found', params: { name: prefill.mapTitle } });
    }
    replayNotice = {
        kind: unresolved.length === 0 && (prefillMap || !prefill.mapTitle) && !prefill.duplicate ? 'ok' : 'warn',
        parts,
    };

    saveState();
    buildUI();
}

// ---------- Этап 1: карта и режим ----------
/** Выбранный сохранённый пресет или null. */
function activePreset(): MapPreset | null {
    return presets.find((p) => p.id === activePresetId) ?? null;
}

/** Грузит пресеты (ошибка не фатальна — инструмент работает и без них). */
async function loadPresets(): Promise<void> {
    try {
        const res = await apiRequest<{ presets: MapPreset[] }>('/api/map-presets');
        presets = res.presets;
    } catch (err) {
        console.error('[tools/match] failed to load presets:', err);
        presets = [];
    }
}

/** Грузит настройки модов (ошибка не фатальна — рандомизация просто не появится). */
async function loadModSettings(): Promise<void> {
    try {
        const mods = await listMods(state.token);
        cachedModSettingsBase = new Map<number, ModSetting>();
        cachedModSettings = new Map<number, ModSetting>();
        cachedModPresets = new Map<number, ModSettingPreset[]>();
        for (const m of mods) {
            if (m.settings && m.settings.params.length > 0) {
                cachedModSettingsBase.set(m.id, m.settings);
                // Личные исключения пользователя накладываются на общие
                cachedModSettings.set(m.id, applyLocalExclusions(m.id, m.settings));
            }
            if (m.presets.length > 0) cachedModPresets.set(m.id, m.presets);
        }
    } catch (err) {
        console.error('[tools/match] failed to load mod settings:', err);
        cachedModSettingsBase = new Map();
        cachedModSettings = new Map();
        cachedModPresets = new Map();
    }
}

/** Разбирает сохранённое значение выбора пресета ('', 'p:<id>', 'd:<type>:<count|all>', 'd:league'). */
function applyPresetValue(value: string): void {
    activePresetId = null;
    activeDynamic = null;
    if (!value) return;

    if (value.startsWith('p:')) {
        const id = Number(value.slice(2));
        if (presets.some((p) => p.id === id)) activePresetId = id;
        return;
    }
    if (value.startsWith('d:')) {
        const [, type, countRaw] = value.split(':');
        if (type === LEAGUE_DYNAMIC_TYPE) {
            // «Все карты FFA Лига» актуален, только если такие карты есть в пуле
            if (dynamicMaps(LEAGUE_DYNAMIC_TYPE, null).length > 0) {
                activeDynamic = { type: LEAGUE_DYNAMIC_TYPE, count: null };
            }
            return;
        }
        if (MAP_TYPES.includes(type as MapType)) {
            const count = countRaw === 'all' ? null : Number(countRaw);
            const sel = { type: type as MapType, count: Number.isInteger(count) ? count : null };
            // Динамический пресет актуален только если по нему есть карты
            if (dynamicMaps(sel.type, sel.count).length > 0) activeDynamic = sel;
        }
        return;
    }
    // Совместимость со старым форматом (просто id сохранённого пресета)
    const legacyId = Number(value);
    if (Number.isInteger(legacyId) && presets.some((p) => p.id === legacyId)) activePresetId = legacyId;
}

/** Текущее значение выбора для <select>. */
function currentPresetValue(): string {
    if (activePresetId !== null) return `p:${activePresetId}`;
    if (activeDynamic) {
        return activeDynamic.type === LEAGUE_DYNAMIC_TYPE
            ? `d:${LEAGUE_DYNAMIC_TYPE}`
            : `d:${activeDynamic.type}:${activeDynamic.count ?? 'all'}`;
    }
    return '';
}

function persistPresetValue(value: string): void {
    try {
        if (!value) localStorage.removeItem(PRESET_KEY);
        else localStorage.setItem(PRESET_KEY, value);
    } catch {
        /* без localStorage */
    }
}

/** Карты, попадающие в динамический пресет (тип + число игроков либо объединение «FFA Лига»). */
function dynamicMaps(type: DynamicType, count: number | null): GameMap[] {
    if (type === LEAGUE_DYNAMIC_TYPE) {
        const ids = leagueMapIds(presets);
        return cachedMaps.filter((m) => ids.has(m.id));
    }
    return cachedMaps.filter(
        (m) => (m.map_types ?? []).includes(type) && (count === null || m.player_count === count)
    );
}

/** Разрешённые id карт или null, если ограничения нет. */
function allowedMapIds(): Set<number> | null {
    const preset = activePreset();
    if (preset) return new Set(preset.map_ids);
    if (activeDynamic) return new Set(dynamicMaps(activeDynamic.type, activeDynamic.count).map((m) => m.id));
    return null;
}

/** Разрешённые id модов или null, если ограничения нет (для списка пресета). */
function allowedModIds(): Set<number> | null {
    const preset = activePreset();
    return preset ? new Set(preset.mod_ids) : null;
}

/**
 * Выбор пресета/динамики «засевает» вычёркнутые карты и моды.
 * Дальше пул можно свободно править в полных настройках (они независимы от пресета).
 */
function seedExclusionsFromPreset(): void {
    const preset = activePreset();
    if (preset) {
        matchExcludedMaps = cachedMaps.filter((m) => !preset.map_ids.includes(m.id)).map((m) => m.id);
        matchExcludedMods = cachedMods.filter((m) => !preset.mod_ids.includes(m.id)).map((m) => m.id);
        return;
    }
    if (activeDynamic) {
        const keep = new Set(dynamicMaps(activeDynamic.type, activeDynamic.count).map((m) => m.id));
        matchExcludedMaps = cachedMaps.filter((m) => !keep.has(m.id)).map((m) => m.id);
        // Динамический пресет ограничивает только карты
        return;
    }
    // «Все карты и моды» — включаем оба пула полностью
    matchExcludedMaps = [];
    matchExcludedMods = [];
}

function mapItems(): ReelItem[] {
    return cachedMaps
        .filter((m) => !matchExcludedMaps.includes(m.id))
        // Дисбалансные карты по умолчанию не участвуют в розыгрыше
        .filter((m) => !(matchSkipUnbalanced && m.unbalanced))
        .map((m) => ({ id: m.id, name: m.name, title: m.alt_name ?? undefined }));
}

function modItems(): ReelItem[] {
    return cachedMods
        .filter((m) => !matchExcludedMods.includes(m.id))
        .map((m) => ({
            id: m.id,
            // Служебный мод NONE показываем как «Без мода»
            name: m.name.trim().toUpperCase() === 'NONE' ? t('tools.random_no_mod') : m.name,
        }));
}

function buildMapStage(): HTMLElement {
    const { section, body } = buildStage('1', 'tools.match_stage_map');
    section.classList.add('match-map-stage');

    // «Настроить»: раскрывает полные настройки пула внизу страницы
    const settingsBtn = document.createElement('button');
    settingsBtn.type = 'button';
    settingsBtn.className = 'btn-secondary match-settings-btn';
    settingsBtn.textContent = t('tools.match_settings');
    settingsBtn.addEventListener('click', openPoolSettings);
    section.querySelector('.match-stage-head')?.appendChild(settingsBtn);

    const machine = document.createElement('div');
    machine.className = 'rr-machine match-machine';
    const reels = document.createElement('div');
    reels.className = 'rr-reels';
    reels.append(mapReel!.el, modReel!.el);
    machine.appendChild(reels);

    const actions = document.createElement('div');
    actions.className = 'match-stage-actions';

    const spinBtn = document.createElement('button');
    spinBtn.type = 'button';
    spinBtn.className = 'btn-primary rr-spin';
    spinBtn.textContent = t('tools.random_spin');

    const skipBtn = document.createElement('button');
    skipBtn.type = 'button';
    skipBtn.className = 'btn-secondary';
    skipBtn.textContent = stageMapSkipped ? t('tools.match_skip_back') : t('tools.match_skip');
    // Восстановленные пропуск/результат розыгрыша
    machine.classList.toggle('is-skipped', stageMapSkipped);
    if (!stageMapSkipped && (selectedMapId !== null || selectedModId !== null)) {
        machine.classList.add('is-done');
    }

    let spinning = false;
    spinBtn.addEventListener('click', () => {
        if (spinning) return;
        spinning = true;
        spinBtn.disabled = true;
        stageMapSkipped = false;
        machine.classList.remove('is-skipped');
        skipBtn.textContent = t('tools.match_skip');
        // OBS-оверлей крутит свои барабаны, пока крутятся барабаны сайта
        void startOverlayRoll({
            maps: mapItems().map((it) => it.name),
            mods: modItems().map((it) => it.name),
        });
        // Мод останавливается позже карты — эффект «по очереди»
        void Promise.all([mapReel!.spin(1800), modReel!.spin(2500)]).then(() => {
            spinning = false;
            spinBtn.disabled = false;
            machine.classList.add('is-done');
            selectedMapId = mapReel?.getSelected()?.id ?? null;
            selectedModId = modReel?.getSelected()?.id ?? null;
            // Новый розыгрыш — настройки мода рандомизируем заново
            modRoll = null;
            saveState();
            // renderMapSummary заодно рандомизирует настройки мода — их тоже на оверлей
            renderMapSummary();
            renderMapImage(true);
            pushMatchRollOverlay();
        });
    });

    skipBtn.addEventListener('click', () => {
        stageMapSkipped = !stageMapSkipped;
        machine.classList.toggle('is-skipped', stageMapSkipped);
        machine.classList.remove('is-done');
        skipBtn.textContent = stageMapSkipped ? t('tools.match_skip_back') : t('tools.match_skip');
        renderMapSummary();
        renderMapImage(false);
        saveState();
        // Пропуск этапа — убираем виджет, возврат — снова показываем выпавшее
        if (stageMapSkipped) void clearOverlayOutput();
        else pushMatchRollOverlay();
    });

    actions.append(spinBtn, skipBtn, buildOverlayToggle((on) => {
        // Включили галочку при готовом результате — сразу выводим его
        if (on) pushMatchRollOverlay();
    }));

    const summary = document.createElement('div');
    summary.className = 'match-map-summary';
    summaryEl = summary;

    // Крупная картинка выпавшей карты (анимированно появляется внизу этапа)
    const mapImage = document.createElement('div');
    mapImage.className = 'match-map-image';
    mapImageEl = mapImage;

    // Названия карты и мода под картинкой
    const pickNames = document.createElement('div');
    pickNames.className = 'match-pick-names';
    pickNamesEl = pickNames;

    // Настройки выпавшего мода (рандомизация параметров)
    const modSettings = document.createElement('div');
    modSettings.className = 'match-mod-settings hidden';
    modSettingsEl = modSettings;

    // Настройка пула — сразу под выбором пресета, свёрнута по умолчанию
    body.append(buildPresetPanel(), buildPoolStage(), machine, actions, summary, mapImage, pickNames, modSettings);
    renderMapSummary();
    renderMapImage(false);
    return section;
}

// ---------- Пресет: выбор набора карт/модов ----------
/** Панель выбора пресета: ограничивает барабаны и выводит набор списком. */
function buildPresetPanel(): HTMLElement {
    const box = document.createElement('div');
    box.className = 'match-preset';
    presetBoxEl = box;

    const head = document.createElement('div');
    head.className = 'match-preset-head';
    const label = document.createElement('label');
    label.className = 'match-preset-label';
    label.textContent = t('tools.match_preset');

    const select = document.createElement('select');
    select.className = 'games-filter-select match-preset-select';
    const all = document.createElement('option');
    all.value = '';
    all.textContent = t('tools.match_preset_all');
    select.appendChild(all);

    // Сохранённые пресеты
    const group = (labelText: string, list: MapPreset[]): void => {
        if (list.length === 0) return;
        const og = document.createElement('optgroup');
        og.label = labelText;
        for (const p of list) {
            const o = document.createElement('option');
            o.value = `p:${p.id}`;
            o.textContent = `${p.name} (${p.map_ids.length}/${p.mod_ids.length})`;
            og.appendChild(o);
        }
        select.appendChild(og);
    };
    group(t('tools.preset_shared'), presets.filter((p) => p.shared));
    group(t('tools.preset_personal'), presets.filter((p) => !p.shared));

    // Динамические пресеты по типу карты и числу игроков (по текущим картам)
    buildDynamicPresetOptions(select);

    select.value = currentPresetValue();
    select.addEventListener('change', () => {
        applyPresetValue(select.value);
        // Пресет задаёт стартовый набор карт/модов; дальше пул правится внизу
        seedExclusionsFromPreset();
        persistPresetValue(select.value);
        // Смена пула: барабаны перерисовывают статичное состояние, прошлый выпавший сбрасывается
        mapReel?.refresh();
        modReel?.refresh();
        stageMapSkipped = false;
        // refresh() мог сбросить выпавшую карту, если её нет в новом пуле
        selectedMapId = mapReel?.getSelected()?.id ?? null;
        selectedModId = modReel?.getSelected()?.id ?? null;
        saveState();
        renderMapSummary();
        renderMapImage(false);
        renderPresetList();
        refreshPoolPickers();
        pushMatchRollOverlay();
    });
    head.append(label, select);

    // Набор пресета скрыт по умолчанию — раскрывается плавно по клику
    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'match-preset-toggle hidden';
    const toggleLabel = document.createElement('span');
    toggleLabel.className = 'match-preset-toggle-label';
    const toggleCount = document.createElement('span');
    toggleCount.className = 'match-preset-toggle-count';
    const toggleChevron = document.createElement('span');
    toggleChevron.className = 'match-preset-toggle-chevron';
    toggleChevron.textContent = '▾';
    toggle.append(toggleLabel, toggleCount, toggleChevron);
    toggle.addEventListener('click', () => {
        presetListOpen = !presetListOpen;
        updatePresetToggle();
    });
    presetToggleEl = toggle;
    presetToggleLabelEl = toggleLabel;
    presetToggleCountEl = toggleCount;

    // Обёртка с grid-rows 0fr → 1fr — плавное раскрытие без фиксированной высоты
    const collapse = document.createElement('div');
    collapse.className = 'match-preset-collapse';
    const list = document.createElement('div');
    list.className = 'match-preset-list';
    presetListEl = list;
    collapse.appendChild(list);

    box.append(head, toggle, collapse);
    renderPresetList();
    return box;
}

/** Состояние кнопки «Показать/скрыть набор пресета». */
function updatePresetToggle(): void {
    const hasSelection = Boolean(activePreset() || activeDynamic);
    presetBoxEl?.classList.toggle('is-open', hasSelection && presetListOpen);
    presetToggleEl?.classList.toggle('hidden', !hasSelection);
    if (presetToggleLabelEl) {
        presetToggleLabelEl.textContent = presetListOpen
            ? t('tools.match_preset_hide')
            : t('tools.match_preset_show');
    }
}

/** Динамические пресеты: для каждого типа карты — «любое число» и конкретные числа игроков; плюс объединение «FFA Лига». */
function buildDynamicPresetOptions(select: HTMLSelectElement): void {
    // «Все карты FFA Лига» — объединение общих пресетов, имя которых начинается с «FFA Лига»
    if (dynamicMaps(LEAGUE_DYNAMIC_TYPE, null).length > 0) {
        const og = document.createElement('optgroup');
        og.label = t('tools.match_preset_dynamic');
        const o = document.createElement('option');
        o.value = `d:${LEAGUE_DYNAMIC_TYPE}`;
        o.textContent = t('tools.preset_league_maps');
        og.appendChild(o);
        select.appendChild(og);
    }

    for (const type of MAP_TYPES) {
        const maps = cachedMaps.filter((m) => (m.map_types ?? []).includes(type));
        if (maps.length === 0) continue;

        const og = document.createElement('optgroup');
        og.label = `${t('tools.match_preset_dynamic')} · ${t(`map_type.${type}` as any)}`;

        const any = document.createElement('option');
        any.value = `d:${type}:all`;
        any.textContent = t('tools.match_preset_any_count');
        og.appendChild(any);

        const counts = [
            ...new Set(maps.map((m) => m.player_count).filter((n): n is number => n != null)),
        ].sort((a, b) => a - b);
        for (const c of counts) {
            const o = document.createElement('option');
            o.value = `d:${type}:${c}`;
            o.textContent = t('tools.random_players', { n: c });
            og.appendChild(o);
        }
        select.appendChild(og);
    }
}

/** Выводит набор карт и модов выбранного пресета списком. */
function renderPresetList(): void {
    if (!presetListEl) return;
    presetListEl.innerHTML = '';
    const hasSelection = Boolean(activePreset() || activeDynamic);
    presetBoxEl?.classList.toggle('is-empty', !hasSelection);
    updatePresetToggle();
    if (!hasSelection) return;

    const keepMaps = allowedMapIds();
    const maps = cachedMaps.filter((m) => !keepMaps || keepMaps.has(m.id));
    const keepMods = allowedModIds();
    const mods = keepMods ? cachedMods.filter((m) => keepMods.has(m.id)) : [];
    if (presetToggleCountEl) presetToggleCountEl.textContent = String(maps.length + mods.length);

    if (maps.length === 0 && mods.length === 0) {
        const empty = document.createElement('div');
        empty.className = 'match-preset-empty';
        empty.textContent = t('tools.match_preset_empty');
        presetListEl.appendChild(empty);
        return;
    }

    if (maps.length) {
        presetListEl.appendChild(buildPresetColumn(t('tools.match_preset_maps'), maps.length));
        // Динамическая строка: столько карт, сколько влезает по ширине названий
        const wrap = document.createElement('div');
        wrap.className = 'match-preset-maps';
        for (const m of maps) wrap.appendChild(buildPresetMapRow(m));
        presetListEl.appendChild(wrap);
    }
    if (mods.length) {
        presetListEl.appendChild(buildPresetColumn(t('tools.match_preset_mods'), mods.length));
        const chips = document.createElement('div');
        chips.className = 'rr-chips match-preset-chips';
        for (const m of mods) {
            const chip = document.createElement('button');
            chip.type = 'button';
            chip.className =
                'rr-chip match-preset-chip' + (matchExcludedMods.includes(m.id) ? ' is-off' : '');
            chip.textContent =
                m.name.trim().toUpperCase() === 'NONE' ? t('tools.random_no_mod') : m.name;
            chip.setAttribute('aria-pressed', String(!matchExcludedMods.includes(m.id)));
            chip.addEventListener('click', () => toggleMatchMod(m.id));
            chips.appendChild(chip);
        }
        presetListEl.appendChild(chips);
    }
}

function buildPresetColumn(title: string, count: number): HTMLElement {
    const row = document.createElement('div');
    row.className = 'match-preset-sub';
    const h = document.createElement('span');
    h.className = 'rr-pool-title';
    const c = document.createElement('span');
    c.className = 'rr-pool-count';
    c.textContent = String(count);
    h.append(title + ' ', c);
    row.appendChild(h);
    return row;
}

/** Компактная строка карты пресета: миниатюра, название; клик вычёркивает карту в матче. */
function buildPresetMapRow(m: GameMap): HTMLElement {
    const off = matchExcludedMaps.includes(m.id);
    const row = document.createElement('button');
    row.type = 'button';
    row.className = 'match-preset-map' + (off ? ' is-off' : '');
    row.title = m.alt_name ?? m.name;
    row.setAttribute('aria-pressed', String(!off));
    row.addEventListener('click', () => toggleMatchMap(m.id));

    const thumb = document.createElement('span');
    thumb.className = 'match-preset-map-thumb';
    const src = m.image_url;
    if (src) {
        const img = document.createElement('img');
        img.src = src;
        img.alt = '';
        img.loading = 'lazy';
        thumb.appendChild(img);
        thumb.addEventListener('mouseenter', () => showMapPreview(src, thumb));
        thumb.addEventListener('mouseleave', hideMapPreview);
    } else {
        thumb.classList.add('is-empty');
        thumb.textContent = '🗺';
    }
    row.appendChild(thumb);

    const name = document.createElement('span');
    name.className = 'match-preset-map-name';
    name.textContent = m.name;
    row.appendChild(name);
    return row;
}

/** Может ли пользователь править исключения настроек (moderator / ghost / админ). */
function canEditMods(): boolean {
    return Boolean(state.user?.is_moderator || state.user?.is_ghost || state.user?.is_admin);
}

/**
 * Окно исключений: модератор сохраняет общие настройки на сервере,
 * обычный пользователь — личные локально. После сохранения значения
 * перерандомизируются по новым правилам.
 */
async function editModExclusions(
    modId: number,
    modName: string,
    rollPresets: ModSettingPreset[]
): Promise<void> {
    const baseSetting = cachedModSettingsBase.get(modId) ?? cachedModSettings.get(modId);
    if (!baseSetting) return;
    const result = await openModExclusionsModal(
        { id: modId, name: modName, settings: baseSetting, presets: rollPresets },
        { canEditShared: canEditMods(), token: state.token }
    );
    if (!result) return;

    // Общие настройки обновляем только при сохранении модератором
    if (result.shared) cachedModSettingsBase.set(modId, result.settings);
    if (result.settings.params.length > 0) cachedModSettings.set(modId, result.settings);
    else cachedModSettings.delete(modId);
    modRoll = rollModSettings(modRoll, modId, cachedModSettings, rollPresets);
    renderModSettings();
    saveState();
    // Исключения меняют значения — обновляем и виджет оверлея
    pushMatchRollOverlay();
}

/**
 * Настройки выпавшего мода: если у мода есть параметры, рандомизируем их
 * (параметры с randomize=false остаются на дефолте) и показываем под названиями.
 */
function renderModSettings(): void {
    const el = modSettingsEl;
    if (!el) return;
    el.innerHTML = '';
    el.classList.add('hidden');

    if (stageMapSkipped) return;
    const modId = selectedModId;
    if (modId == null || !cachedModSettings.has(modId)) {
        modRoll = null;
        return;
    }

    // Розыгрыш мог смениться — настройки другого мода не показываем
    if (!modRoll || modRoll.modId !== modId) {
        modRoll = rollModSettings(null, modId, cachedModSettings, cachedModPresets.get(modId) ?? []);
        scheduleStateSave();
    }

    const roll = modRoll;
    if (!roll) return;

    const mod = cachedMods.find((m) => m.id === roll.modId);
    const rollPresets = cachedModPresets.get(roll.modId) ?? [];
    el.appendChild(
        buildModSettingsBox(roll, mod ? modLabel(mod) : '', rollPresets, {
            // Окно исключений доступно всем: модератор сохраняет общие,
            // пользователь — личные (локально)
            onExclusions: () =>
                void editModExclusions(roll.modId, mod ? modLabel(mod) : '', rollPresets),
            exclusions: countModExclusions(cachedModSettings.get(roll.modId)),
            onReroll: () => {
                if (!modRoll) return;
                modRoll = rollModSettings(modRoll, modRoll.modId, cachedModSettings, rollPresets);
                renderModSettings();
                saveState();
                // Настройки мода — часть виджета оверлея
                pushMatchRollOverlay();
            },
            onPreset: (preset) => {
                if (!modRoll) return;
                modRoll = presetToRoll(modRoll.modId, preset, cachedModSettings) ?? modRoll;
                renderModSettings();
                saveState();
                // Настройки мода — часть виджета оверлея
                pushMatchRollOverlay();
            },
            onToggle: (name) => {
                if (!modRoll) return;
                modRoll = toggleModSetting(modRoll, name, cachedModSettings) ?? modRoll;
                renderModSettings();
                saveState();
                // Настройки мода — часть виджета оверлея
                pushMatchRollOverlay();
            },
            onMode: (mode) => {
                if (!modRoll) return;
                modRoll = rollModSettings(modRoll, modRoll.modId, cachedModSettings, rollPresets, mode);
                renderModSettings();
                saveState();
                // Настройки мода — часть виджета оверлея
                pushMatchRollOverlay();
            },
        })
    );
    el.classList.remove('hidden');
}

function renderMapSummary(): void {
    renderPickNames();
    renderModSettings();
    if (!summaryEl) return;
    summaryEl.innerHTML = '';
    if (stageMapSkipped) {
        summaryEl.textContent = t('tools.match_skipped');
        summaryEl.classList.add('is-skipped');
        return;
    }
    summaryEl.classList.remove('is-skipped');
    const map = mapReel?.getSelected() ?? null;
    const mod = modReel?.getSelected() ?? null;
    if (!map && !mod) {
        summaryEl.textContent = t('tools.match_no_pick');
        return;
    }
    if (map) {
        const chip = document.createElement('span');
        chip.className = 'match-pick match-pick--map';
        chip.textContent = `${t('tools.random_map')}: ${map.name}`;
        summaryEl.appendChild(chip);
    }
    if (mod) {
        const chip = document.createElement('span');
        chip.className = 'match-pick match-pick--mod';
        chip.textContent = `${t('tools.random_mod')}: ${mod.name}`;
        summaryEl.appendChild(chip);
    }
}

/**
 * Названия выпавших карты и мода под картинкой:
 * карта — крупно, мод — чуть меньше и другим цветом.
 */
function renderPickNames(): void {
    const el = pickNamesEl;
    if (!el) return;
    el.innerHTML = '';
    el.classList.remove('is-visible');
    if (stageMapSkipped) return;

    const map = mapReel?.getSelected() ?? null;
    const mod = modReel?.getSelected() ?? null;
    if (!map && !mod) return;

    if (map) {
        const name = document.createElement('div');
        name.className = 'match-pick-map-name';
        name.textContent = map.name;
        el.appendChild(name);
        // Ссылка карты: клик копирует её (как в разделе «Карты»)
        const cached = cachedMaps.find((m) => m.id === map.id);
        if (cached?.link) el.appendChild(buildLinkCopyButton(cached.link, 'map-card-link match-pick-link', true));
    }
    if (mod) {
        const name = document.createElement('div');
        name.className = 'match-pick-mod-name';
        name.textContent = mod.name;
        el.appendChild(name);
    }
    el.classList.add('is-visible');
}

/**
 * Показывает картинку выпавшей карты внизу этапа (если она есть).
 * animate=true — проиграть анимацию появления (после розыгрыша).
 */
function renderMapImage(animate: boolean): void {
    const el = mapImageEl;
    if (!el) return;
    el.classList.remove('is-visible', 'is-animated');
    el.innerHTML = '';

    const map = stageMapSkipped ? null : mapReel?.getSelected() ?? null;
    const cached = map ? cachedMaps.find((m) => m.id === map.id) : undefined;
    if (!cached?.image_url) return;

    const img = document.createElement('img');
    img.src = cached.image_url;
    img.alt = cached.name;
    el.appendChild(img);
    el.classList.add('is-visible');
    if (animate) {
        void el.offsetWidth; // reflow — чтобы анимация перезапускалась при новом розыгрыше
        el.classList.add('is-animated');
    }
}

/**
 * Данные выпавшей карты/мода для виджета OBS-оверлея
 * (тот же виджет, что у рандомайзера на /tools/random).
 */
function buildOverlayRoll(): MapRollOutput | null {
    if (stageMapSkipped) return null;
    const map = mapReel?.getSelected() ?? null;
    const cached = map ? cachedMaps.find((m) => m.id === map.id) : undefined;
    if (!cached) return null;

    const mod = modReel?.getSelected() ?? null;
    return {
        mapName: cached.name,
        mapAlt: cached.alt_name ?? null,
        mapImage: cached.image_url ?? null,
        modName: mod?.name ?? null,
        playerCount: cached.player_count ?? null,
        types: mapFilterTypes(cached),
        // На оверлей — только включённые (рандомизированные) параметры мода
        settings:
            modRoll && modRoll.modId === selectedModId
                ? modRoll.params
                      .filter((p) => p.enabled)
                      .map((p) => ({ name: p.name, value: p.value }))
                : [],
    };
}

/** Отправляет выпавший вариант на оверлей (или убирает виджет, если нечего показывать). */
function pushMatchRollOverlay(): void {
    const data = buildOverlayRoll();
    if (data) void showOverlayRoll(data);
    else void clearOverlayOutput();
}

// ---------- Нижняя секция: настройка пула (общий модуль с рандомайзером) ----------
const modLabel = (m: GameMod): string =>
    m.name.trim().toUpperCase() === 'NONE' ? t('tools.random_no_mod') : m.name;

/** Клик по карте/моду вычёркивает его только в этом матче (списки/пресет не меняются). */
function toggleMatchMap(id: number): void {
    matchExcludedMaps = matchExcludedMaps.includes(id)
        ? matchExcludedMaps.filter((x) => x !== id)
        : [...matchExcludedMaps, id];
    afterPoolChange();
}

function toggleMatchMod(id: number): void {
    matchExcludedMods = matchExcludedMods.includes(id)
        ? matchExcludedMods.filter((x) => x !== id)
        : [...matchExcludedMods, id];
    afterPoolChange();
}

/** Перечитывает состояния пула (после смены пресета/восстановления состояния). */
function refreshPoolPickers(): void {
    for (const picker of poolPickers) picker.refresh();
}

/** После изменения пула: барабаны, выпавший результат, список пресета, настройки. */
function afterPoolChange(): void {
    mapReel?.refresh();
    modReel?.refresh();
    selectedMapId = mapReel?.getSelected()?.id ?? null;
    selectedModId = modReel?.getSelected()?.id ?? null;
    renderMapSummary();
    renderMapImage(false);
    renderPresetList();
    refreshPoolPickers();
    saveState();
    // Пул изменился — выпавшая карта/мод могли смениться
    pushMatchRollOverlay();
}

/** Кнопка «Настроить» на этапе 1: раскрыть низ и проскроллить к нему. */
function openPoolSettings(): void {
    if (!poolDetailsEl) return;
    poolDetailsEl.open = true;
    requestAnimationFrame(() => poolDetailsEl?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
}

/** Типы карты для показа/фильтра: ручные + вычисляемые из общих списков лиги. */
function mapFilterTypes(m: GameMap): MapFilterType[] {
    return mapTypesWithLeague(m, leagueMapTypes(presets));
}

function buildPoolStage(): HTMLElement {
    const details = document.createElement('details');
    details.className = 'card match-stage match-pool-stage';
    poolDetailsEl = details;

    const summary = document.createElement('summary');
    summary.className = 'match-stage-head match-pool-summary';
    const title = document.createElement('h3');
    title.className = 'match-stage-title';
    title.textContent = t('tools.match_stage_pool');
    const chevron = document.createElement('span');
    chevron.className = 'match-pool-chevron';
    chevron.textContent = '▾';
    summary.append(title, chevron);
    details.appendChild(summary);

    const body = document.createElement('div');
    body.className = 'match-stage-body match-pool-body';

    const hint = document.createElement('div');
    hint.className = 'match-pool-hint';
    hint.textContent = t('tools.match_pool_hint');

    // Полные настройки пула — те же, что в рандомайзере: слева включение
    // в розыгрыш и фильтр списка, справа крупный список карт / чипы модов.
    const mapPicker = buildPoolPicker({
        kind: 'maps',
        items: cachedMaps.map(mapToPoolItem),
        presets,
        getExcluded: () => matchExcludedMaps,
        setExcluded: (ids) => {
            matchExcludedMaps = ids;
        },
        onChange: afterPoolChange,
        viewSwitch: true,
        skipUnbalanced: {
            get: () => matchSkipUnbalanced,
            set: (value) => {
                matchSkipUnbalanced = value;
                scheduleStateSave();
            },
        },
    });
    const modPicker = buildPoolPicker({
        kind: 'mods',
        items: cachedMods.map(modToPoolItem),
        presets,
        getExcluded: () => matchExcludedMods,
        setExcluded: (ids) => {
            matchExcludedMods = ids;
        },
        onChange: afterPoolChange,
    });
    poolPickers = [mapPicker, modPicker];

    const poolsBox = document.createElement('div');
    poolsBox.className = 'rr-pools';
    poolsBox.append(mapPicker.el, modPicker.el);

    body.append(hint, poolsBox);
    details.appendChild(body);
    return details;
}

// ---------- Этап 2: участники ----------
function buildPlayersStage(): HTMLElement {
    const { section, body } = buildStage('2', 'tools.match_stage_players');

    const box = document.createElement('div');
    box.id = PLAYERS_BOX_ID;
    box.className = 'predict-players';
    playersBox = box;

    const addBtn = document.createElement('button');
    addBtn.type = 'button';
    addBtn.className = 'btn-secondary match-add-player';
    addBtn.textContent = t('games.add_player');
    addBtn.addEventListener('click', () => {
        matchPlayers.push({ id: null, name: '', race: null });
        renderPlayers();
        refreshStreamerHint();
        scheduleStateSave();
    });

    body.append(box, addBtn);
    return section;
}

function renderPlayers(): void {
    if (!playersBox) return;
    playersBox.innerHTML = '';
    matchPlayers.forEach((sp, index) => playersBox!.appendChild(buildPlayerRow(sp, index)));
}

function buildPlayerRow(sp: MatchPlayer, index: number): HTMLElement {
    const row = document.createElement('div');
    row.className = 'player-entry predict-player-row match-player-row';

    // --- Автокомплит игрока ---
    const wrap = document.createElement('div');
    wrap.className = 'player-autocomplete';
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'player-name-input';
    input.placeholder = t('games.player_placeholder');
    input.autocomplete = 'off';
    input.value = sp.name;

    const dropdown = document.createElement('div');
    dropdown.className = 'player-autocomplete-dropdown hidden';
    wrap.append(input, dropdown);
    row.appendChild(wrap);

    let highlighted = -1;
    let matches: PlayerWithStats[] = [];

    const hide = (): void => {
        dropdown.classList.add('hidden');
        highlighted = -1;
    };

    const select = (p: PlayerWithStats): void => {
        const race = matchPlayers[index]?.race ?? p.dominant_race ?? null;
        matchPlayers[index] = { id: p.id, name: p.name, race };
        raceControl.setRace(race);
        input.value = p.name;
        hide();
        refreshStreamerHint();
        scheduleStateSave();
    };

    const show = (query: string): void => {
        const q = query.trim().toLowerCase();
        if (!q) {
            matches = [];
            hide();
            return;
        }
        matches = cachedPlayers
            .filter((p) => p.name.toLowerCase().includes(q) || (p.aka ?? '').toLowerCase().includes(q))
            .slice(0, 10);
        dropdown.innerHTML = '';
        if (matches.length === 0) {
            const empty = document.createElement('div');
            empty.className = 'player-autocomplete-empty';
            empty.textContent = t('games.player_not_found');
            dropdown.appendChild(empty);
            dropdown.classList.remove('hidden');
            return;
        }
        matches.forEach((p, i) => {
            const d = document.createElement('div');
            d.className = 'player-autocomplete-item' + (i === highlighted ? ' is-highlighted' : '');
            const name = document.createElement('span');
            name.className = 'player-autocomplete-name';
            name.textContent = p.name;
            d.appendChild(name);
            if (p.aka) {
                const aka = document.createElement('span');
                aka.className = 'player-autocomplete-aka';
                aka.textContent = ` aka ${p.aka}`;
                d.appendChild(aka);
            }
            d.addEventListener('mousedown', (e) => {
                e.preventDefault();
                select(p);
            });
            dropdown.appendChild(d);
        });
        dropdown.classList.remove('hidden');
    };

    input.addEventListener('input', () => {
        matchPlayers[index] = { id: null, name: input.value, race: matchPlayers[index]?.race ?? null };
        highlighted = -1;
        show(input.value);
        refreshStreamerHint();
        scheduleStateSave();
    });
    input.addEventListener('focus', () => {
        if (input.value.trim()) show(input.value);
    });
    input.addEventListener('blur', () => setTimeout(hide, 150));
    input.addEventListener('keydown', (e) => {
        if (dropdown.classList.contains('hidden')) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            highlighted = Math.min(highlighted + 1, matches.length - 1);
            show(input.value);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            highlighted = Math.max(highlighted - 1, -1);
            show(input.value);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const picked = matches[highlighted];
            if (picked) select(picked);
        } else if (e.key === 'Escape') {
            hide();
        }
    });

    // --- Раса (общий переключатель из инструмента прогноза) ---
    const raceControl = buildRacePicker(sp.race, (race) => {
        matchPlayers[index] = { ...matchPlayers[index]!, race };
        scheduleStateSave();
    });
    row.appendChild(raceControl.element);

    // --- Удалить ---
    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'remove-player';
    removeBtn.textContent = '✕';
    removeBtn.title = t('games.remove_player');
    removeBtn.addEventListener('click', () => {
        matchPlayers.splice(index, 1);
        renderPlayers();
        refreshStreamerHint();
        scheduleStateSave();
    });
    row.appendChild(removeBtn);

    return row;
}

/** Последний прогноз (для повторного вывода на оверлей при включении галочки). */
let lastPredictRows: PredictResultRow[] = [];

// ---------- Этап 3: прогноз ----------
function buildPredictStage(): HTMLElement {
    const { section, body } = buildStage('3', 'tools.match_stage_predict');

    const runBtn = document.createElement('button');
    runBtn.type = 'button';
    runBtn.className = 'btn-primary';
    runBtn.textContent = t('tools.predict_run');
    runBtn.addEventListener('click', () => void runPrediction(runBtn));

    const err = document.createElement('div');
    err.className = 'error hidden';
    predictErrorBox = err;

    const results = document.createElement('div');
    results.className = 'match-predict-results hidden';
    predictResultsBox = results;

    // Прогноз можно сразу выводить на OBS-оверлей
    const overlayToggle = buildOverlayToggle((on) => {
        // Включили галочку при уже рассчитанном прогнозе — выводим его сразу
        if (on) pushMatchPredictOverlay(lastPredictRows, lastDrawnWinner);
    });
    overlayToggle.classList.add('predict-overlay-toggle');

    body.append(runBtn, overlayToggle, err, results);
    return section;
}

async function runPrediction(btn: HTMLElement): Promise<void> {
    predictErrorBox?.classList.add('hidden');

    const playerIds = matchPlayers.map((p) => p.id).filter((id): id is number => id !== null);
    if (playerIds.length < 2) {
        if (predictErrorBox) {
            predictErrorBox.textContent = t('tools.predict_need_two');
            predictErrorBox.classList.remove('hidden');
        }
        return;
    }

    const mapId = stageMapSkipped ? null : mapReel?.getSelected()?.id ?? null;
    const modId = stageMapSkipped ? null : modReel?.getSelected()?.id ?? null;

    const original = btn.textContent;
    btn.textContent = t('common.loading');
    btn.setAttribute('disabled', '');
    try {
        const rows = await fetchPrediction(playerIds, mapId, modId);
        lastPredictRows = rows;

        const winner = drawWeightedWinner(rows);
        lastDrawnWinner = winner;

        // Строки без кубика — сначала прокрутка подсветки, потом победитель
        if (predictResultsBox) {
            predictResultsBox.classList.remove('hidden');
            renderPredictResults(predictResultsBox, rows, null);
        }
        // Оверлей: прокрутка подсветки строк, финал — по окончании анимации
        pushMatchPredictOverlay(rows, winner, 'rolling');

        const winnerIndex = winner === null ? -1 : rows.findIndex((r) => r.player_id === winner);
        cancelDraw?.();
        cancelDraw = null;
        if (predictResultsBox && winnerIndex >= 0) {
            const box = predictResultsBox;
            cancelDraw = animateDrawRows(box, '.predict-chart-row', rows.length, winnerIndex, () => {
                cancelDraw = null;
                renderPredictResults(box, rows, winner);
                pushMatchPredictOverlay(rows, winner, 'result');
            });
        }
    } catch (err) {
        if (predictErrorBox) {
            predictErrorBox.textContent =
                t('tools.predict_error') + ' ' + (err instanceof Error ? err.message : String(err));
            predictErrorBox.classList.remove('hidden');
        }
    } finally {
        btn.textContent = original;
        btn.removeAttribute('disabled');
    }
}

/** Прогноз для виджета оверлея: выпавшая карта/мод и расы участников. */
function pushMatchPredictOverlay(
    rows: PredictResultRow[],
    drawn: number | null,
    phase: 'rolling' | 'result' = 'result'
): void {
    if (rows.length < 2) return;

    const map = stageMapSkipped ? null : mapReel?.getSelected() ?? null;
    const mod = stageMapSkipped ? null : modReel?.getSelected() ?? null;
    const races = new Map<string, Race | null>(
        matchPlayers.map((p) => [p.name, p.race] as [string, Race | null])
    );

    const data: MatchPredictOutput = {
        mapName: map?.name ?? null,
        modName: mod?.name ?? null,
        players: rows.map((r) => ({
            name: r.name,
            race: races.get(r.name) ?? null,
            chance: r.win_chance,
            place: r.place,
            drawn: r.player_id === drawn,
            games: r.games_played,
            winrate: r.winrate,
        })),
    };
    void (phase === 'rolling' ? startOverlayPredictionDraw(data) : showOverlayPrediction(data));
}

// ---------- Этап 4: режим стримера ----------
function buildStreamerStage(): HTMLElement {
    const { section, body } = buildStage('4', 'tools.match_stage_stream');

    const openBtn = document.createElement('button');
    openBtn.type = 'button';
    openBtn.className = 'btn-secondary match-streamer-btn';
    openBtn.textContent = `🎬 ${t('tools.match_open_streamer')}`;
    openBtn.addEventListener('click', () => openStreamerMode());

    const hint = document.createElement('div');
    hint.className = 'match-streamer-hint';
    streamerHintEl = hint;

    body.append(openBtn, hint);
    refreshStreamerHint();
    return section;
}

/** Игроки с выбранным id — их и закрепляем в режиме стримера. */
function pinnedIds(): number[] {
    return matchPlayers.map((p) => p.id).filter((id): id is number => id !== null);
}

function refreshStreamerHint(): void {
    if (streamerHintEl) {
        const count = pinnedIds().length;
        streamerHintEl.textContent =
            count > 0 ? t('tools.match_streamer_hint', { n: count }) : t('tools.match_streamer_no_players');
    }
    // Внизу тоже зависит от числа сопоставленных игроков
    refreshAddGameState();
}

function openStreamerMode(): void {
    const ids = pinnedIds();
    const query = ids.length ? `?pin=${ids.join(',')}` : '';
    // sessionStorage — подстраховка для перехода в этой же вкладке
    try {
        sessionStorage.setItem('control.pinned', JSON.stringify(ids));
    } catch {
        /* ignore */
    }
    window.open(`/control${query}`, '_blank');
}

// ---------- Этап 5: добавить игру в список ----------
/** Кнопку может использовать только модератор/админ — как и создание игры. */
function canAddGame(): boolean {
    return Boolean(state.user?.is_moderator || state.user?.is_admin);
}

function buildAddGameStage(): HTMLElement {
    const { section, body } = buildStage('5', 'tools.match_stage_add_game');

    const row = document.createElement('div');
    row.className = 'match-add-game-row';

    const dateLabel = document.createElement('label');
    dateLabel.className = 'match-add-game-label';
    const dateText = document.createElement('span');
    dateText.textContent = t('tools.match_game_date');
    const dateInput = document.createElement('input');
    dateInput.type = 'datetime-local';
    dateInput.className = 'match-add-game-date';
    dateInput.value = addGameAt;
    dateInput.addEventListener('input', () => {
        addGameAt = dateInput.value;
        scheduleStateSave();
    });
    addGameAtInput = dateInput;
    dateLabel.append(dateText, dateInput);

    const addBtn = document.createElement('button');
    addBtn.type = 'button';
    addBtn.className = 'btn-primary match-add-game-btn';
    addBtn.textContent = `➕ ${t('tools.match_add_game')}`;
    addBtn.addEventListener('click', () => addGameToList());

    const hint = document.createElement('div');
    hint.className = 'match-add-game-hint';
    addGameHintEl = hint;

    row.append(dateLabel, addBtn);
    body.append(row, hint, buildReplayImportPanel());
    refreshAddGameState();
    return section;
}

/** Подсказка и доступность кнопки: права + число сопоставленных игроков. */
function refreshAddGameState(): void {
    if (!addGameHintEl) return;
    const btn = addGameHintEl.parentElement?.querySelector<HTMLButtonElement>('.match-add-game-btn');
    const dateInput = addGameAtInput;
    if (!canAddGame()) {
        addGameHintEl.textContent = t('tools.match_add_game_forbidden');
        if (btn) btn.disabled = true;
        if (dateInput) dateInput.disabled = true;
        return;
    }
    if (btn) btn.disabled = false;
    if (dateInput) dateInput.disabled = false;
    const count = pinnedIds().length;
    addGameHintEl.textContent = count >= 2
        ? t('tools.match_add_game_hint', { n: count })
        : t('tools.match_add_game_no_players');
}

/** Собирает черновик игры и открывает форму новой игры на /games. */
function addGameToList(): void {
    if (!canAddGame()) return;
    const players = matchPlayers
        .filter((p): p is MatchPlayer & { id: number } => p.id !== null)
        .map((p) => ({ player_id: p.id, race: (p.race ?? 'T') as Race }));
    if (players.length < 2) {
        if (addGameHintEl) addGameHintEl.textContent = t('tools.match_add_game_no_players');
        return;
    }

    const draft: MatchGamePrefill = {
        played_at: addGameAt || toDatetimeLocal(new Date()),
        map_id: stageMapSkipped ? null : (mapReel?.getSelected()?.id ?? null),
        mod_id: stageMapSkipped ? null : (modReel?.getSelected()?.id ?? null),
        players,
        ...(replayLobbySettings.length > 0 ? { lobby_settings: replayLobbySettings } : {}),
    };
    try {
        sessionStorage.setItem(MATCH_PREFILL_KEY, JSON.stringify(draft));
    } catch {
        /* без sessionStorage форма просто откроется пустой */
    }
    replayLobbySettings = [];
    navigateTo('/games?create=match');
}
